import {
  BoardDefError,
  areNeighbours,
  cellDistance,
  fromCube,
  gridOf,
  hexagonCells,
  hexagonRadius,
  maxFactions,
  toCube,
  validateBoardDef,
  type BoardDef,
  type FactionDef,
  type GridType,
  type PlacementDef,
} from '@archon3d/core';
import { useSyncExternalStore } from 'react';

/** The characters of a row string, as the paint tool paints them. */
export type CellChar = 'L' | 'D' | '~' | '#';
export type Tool = 'paint' | 'power' | 'wall' | 'place';

export interface Coord {
  x: number;
  y: number;
}

export const MIN_SIZE = 3;
export const MAX_SIZE = 16;
/** Rings around the centre of a hexagon-shaped hex board: 5×5 (19 hexes) to 15×15 (169). */
export const MIN_RADIUS = 2;
export const MAX_RADIUS = 7;

/** Sides in seat order; a new board starts with the first two. */
export const FACTION_DEFAULTS: readonly FactionDef[] = [
  { id: 'light', name: 'Light', color: '#f4ead0', affinity: 'light' },
  { id: 'dark', name: 'Dark', color: '#8e2437', affinity: 'dark' },
  { id: 'grey', name: 'Grey', color: '#9aa4bd', affinity: 'neutral' },
  { id: 'red', name: 'Red', color: '#d9482b', affinity: 'neutral' },
  { id: 'green', name: 'Green', color: '#3f9a4d', affinity: 'neutral' },
  { id: 'blue', name: 'Blue', color: '#3b64d8', affinity: 'neutral' },
];

export interface EditorState {
  def: BoardDef;
  tool: Tool;
  /** What the paint tool paints. */
  brush: CellChar;
  /** Which side (index into `def.factions`) and character the place tool puts down. */
  faction: number;
  character: string;
  /** The first cell picked by the wall tool, waiting for its neighbour. */
  wallFrom: Coord | null;
  /** Changes since the board was created, loaded or saved. */
  dirty: boolean;
}

export interface NewBoardOptions {
  name: string;
  grid: GridType;
  width: number;
  height: number;
  /** Rings around the centre of a hex board: given, the board is a hexagon and its size follows. */
  radius?: number;
  /** Ids already in use (bundled and saved boards), so the new id is unique. */
  takenIds: readonly string[];
}

export type EditorAction =
  | { type: 'undo' | 'redo' }
  | ({ type: 'new' } & NewBoardOptions)
  | { type: 'load'; def: BoardDef }
  | { type: 'saved' }
  | { type: 'tool'; tool: Tool }
  | { type: 'brush'; brush: CellChar }
  | { type: 'piece'; faction?: number; character?: string }
  | { type: 'character'; character: string }
  /** `stroke`: a cell the pointer was dragged onto, painted as part of the press before it. */
  | { type: 'cell'; x: number; y: number; stroke?: boolean }
  | { type: 'rename'; name: string }
  | { type: 'describe'; description: string }
  | { type: 'luminosity'; value: BoardDef['luminosity'] }
  | { type: 'resize'; width: number; height: number }
  | { type: 'radius'; radius: number }
  | { type: 'grid'; grid: GridType }
  | { type: 'add-faction' }
  | { type: 'remove-faction' }
  | { type: 'faction'; index: number; patch: Partial<Omit<FactionDef, 'id'>> };

/** `Light`, `Dark`, then the three-colour hex tiling or a chequerboard as the starting pattern. */
function fillChar(grid: GridType, x: number, y: number): CellChar {
  if (grid === 'square') return (x + y) % 2 === 0 ? 'L' : 'D';
  // Three colours suffice on a hex grid; in cube coordinates q − r tells them apart.
  const q = x - (y - (y & 1)) / 2;
  const k = (((q - y) % 3) + 3) % 3;
  return k === 0 ? 'L' : k === 1 ? 'D' : '~';
}

export function slugify(name: string): string {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
  return slug.length >= 2 ? slug : `board-${slug}`.slice(0, 48);
}

/** `base`, or `base-2`, `base-3`, … until it clashes with nothing in `taken`. */
export function uniqueId(base: string, taken: readonly string[]): string {
  if (!taken.includes(base)) return base;
  for (let n = 2; ; n++) {
    const candidate = `${base}-${n}`;
    if (!taken.includes(candidate)) return candidate;
  }
}

/** A hexagon of hexes by default; a rectangle of them only when asked for by width and height. */
export function newBoardDef({
  name,
  grid,
  width,
  height,
  radius,
  takenIds,
}: NewBoardOptions): BoardDef {
  const hexagon = grid === 'hex' && radius !== undefined ? clampRadius(radius) : undefined;
  const w = hexagon === undefined ? clampSize(width) : 2 * hexagon + 1;
  const h = hexagon === undefined ? clampSize(height) : 2 * hexagon + 1;
  const cells =
    hexagon === undefined
      ? Array.from({ length: h }, (_, y) =>
          Array.from({ length: w }, (_, x) => fillChar(grid, x, y)).join(''),
        )
      : hexagonCells(hexagon, (x, y) => fillChar(grid, x, y));
  const def: BoardDef = {
    id: uniqueId(slugify(name), takenIds),
    name: name.trim() || 'Untitled board',
    width: w,
    height: h,
    cells,
    powerPoints: [],
    walls: [],
    factions: FACTION_DEFAULTS.slice(0, 2).map((f) => ({ ...f })),
    placements: [],
  };
  return grid === 'hex' ? { ...def, grid } : def;
}

function clampSize(n: number): number {
  return Math.max(MIN_SIZE, Math.min(MAX_SIZE, Math.round(n) || MIN_SIZE));
}

function clampRadius(n: number): number {
  return Math.max(MIN_RADIUS, Math.min(MAX_RADIUS, Math.round(n) || MIN_RADIUS));
}

function dims(def: BoardDef) {
  return { width: def.width, height: def.height, def };
}

/**
 * The radius the editor's radius control shows for a hex board: the hexagon's own, or for a
 * rectangle of hexes the one `setHexRadius` would build around its middle.
 */
export function hexRadiusOf(def: BoardDef): number {
  return hexagonRadius(def) ?? clampRadius(Math.floor((Math.min(def.width, def.height) - 1) / 2));
}

function charAt(def: BoardDef, x: number, y: number): CellChar {
  return (def.cells[y]?.[x] ?? '#') as CellChar;
}

function inside(def: BoardDef, x: number, y: number): boolean {
  return (
    Number.isInteger(x) &&
    Number.isInteger(y) &&
    x >= 0 &&
    y >= 0 &&
    x < def.width &&
    y < def.height
  );
}

const same = (a: Coord, b: Coord) => a.x === b.x && a.y === b.y;

/** Paints one cell; a cell turned to void loses its power point, wall ends and piece. */
export function paintCell(def: BoardDef, x: number, y: number, brush: CellChar): BoardDef {
  if (!inside(def, x, y) || charAt(def, x, y) === brush) return def;
  const cells = def.cells.map((row, ry) =>
    ry === y ? row.slice(0, x) + brush + row.slice(x + 1) : row,
  );
  const next = { ...def, cells };
  if (brush !== '#') return next;
  const here = { x, y };
  return {
    ...next,
    powerPoints: next.powerPoints.filter(([px, py]) => !same({ x: px, y: py }, here)),
    walls: (next.walls ?? []).filter(
      ([[ax, ay], [bx, by]]) => !same({ x: ax, y: ay }, here) && !same({ x: bx, y: by }, here),
    ),
    placements: next.placements.filter((p) => !same(p, here)),
  };
}

export function togglePowerPoint(def: BoardDef, x: number, y: number): BoardDef {
  if (!inside(def, x, y) || charAt(def, x, y) === '#') return def;
  const without = def.powerPoints.filter(([px, py]) => px !== x || py !== y);
  return {
    ...def,
    powerPoints: without.length === def.powerPoints.length ? [...without, [x, y]] : without,
  };
}

/** Adds the wall between two adjacent floor cells, or removes it if it is there. */
export function toggleWall(def: BoardDef, a: Coord, b: Coord): BoardDef {
  if (!inside(def, a.x, a.y) || !inside(def, b.x, b.y)) return def;
  if (charAt(def, a.x, a.y) === '#' || charAt(def, b.x, b.y) === '#') return def;
  if (!areNeighbours({ width: def.width, height: def.height, def }, a, b)) return def;
  const walls = def.walls ?? [];
  const without = walls.filter(
    ([[ax, ay], [bx, by]]) =>
      !(
        (ax === a.x && ay === a.y && bx === b.x && by === b.y) ||
        (ax === b.x && ay === b.y && bx === a.x && by === a.y)
      ),
  );
  return {
    ...def,
    walls:
      without.length === walls.length
        ? [
            ...without,
            [
              [a.x, a.y],
              [b.x, b.y],
            ],
          ]
        : without,
  };
}

/**
 * Puts a side's character on a floor cell, replacing whatever stood there; putting the same piece
 * down twice clears the cell.
 */
export function placePiece(
  def: BoardDef,
  x: number,
  y: number,
  faction: string,
  character: string,
): BoardDef {
  if (!inside(def, x, y) || charAt(def, x, y) === '#') return def;
  const here = { x, y };
  const existing = def.placements.find((p) => same(p, here));
  const rest = def.placements.filter((p) => !same(p, here));
  if (existing && existing.faction === faction && existing.character === character)
    return { ...def, placements: rest };
  const placement: PlacementDef = { faction, character, x, y };
  return { ...def, placements: [...rest, placement] };
}

export function addFaction(def: BoardDef): BoardDef {
  const cap = maxFactions(gridOf({ width: def.width, height: def.height, def }));
  if (def.factions.length >= cap) return def;
  const taken = def.factions.map((f) => f.id);
  const template =
    FACTION_DEFAULTS.find((f) => !taken.includes(f.id)) ?? FACTION_DEFAULTS[def.factions.length]!;
  return {
    ...def,
    factions: [...def.factions, { ...template, id: uniqueId(template.id, taken) }],
  };
}

/** Removes the highest-numbered side and its pieces; a board keeps at least two. */
export function removeFaction(def: BoardDef): BoardDef {
  if (def.factions.length <= 2) return def;
  const last = def.factions[def.factions.length - 1]!;
  return {
    ...def,
    factions: def.factions.slice(0, -1),
    placements: def.placements.filter((p) => p.faction !== last.id),
  };
}

export function updateFaction(
  def: BoardDef,
  index: number,
  patch: Partial<Omit<FactionDef, 'id'>>,
): BoardDef {
  if (!def.factions[index]) return def;
  return {
    ...def,
    factions: def.factions.map((f, i) => (i === index ? { ...f, ...patch } : f)),
  };
}

/** Grows or crops the board from its north-west corner; new cells get the starting pattern. */
export function resizeBoard(def: BoardDef, width: number, height: number): BoardDef {
  const w = clampSize(width);
  const h = clampSize(height);
  if (w === def.width && h === def.height) return def;
  const grid = gridOf({ width: def.width, height: def.height, def });
  const cells = Array.from({ length: h }, (_, y) =>
    Array.from({ length: w }, (_, x) =>
      x < def.width && y < def.height ? charAt(def, x, y) : fillChar(grid, x, y),
    ).join(''),
  );
  const fits = (c: Coord) => c.x < w && c.y < h;
  return {
    ...def,
    width: w,
    height: h,
    cells,
    powerPoints: def.powerPoints.filter(([x, y]) => fits({ x, y })),
    walls: (def.walls ?? []).filter(
      ([[ax, ay], [bx, by]]) => fits({ x: ax, y: ay }) && fits({ x: bx, y: by }),
    ),
    placements: def.placements.filter(fits),
  };
}

/**
 * Makes a hex board a hexagon of `radius` rings around its centre cell, keeping what overlaps: a
 * hexagon grows or shrinks by whole rings (new rings get the starting pattern, the designer's
 * void hexes inside stay void) and a rectangle of hexes becomes the hexagon around its middle.
 * Cells move by a cube-coordinate translation, so neighbours – and the walls between them –
 * stay neighbours whatever the row parity of the new centre.
 */
export function setHexRadius(def: BoardDef, radius: number): BoardDef {
  if (gridOf(dims(def)) !== 'hex') return def;
  const r = clampRadius(radius);
  const oldRadius = hexagonRadius(def);
  if (oldRadius === r) return def;
  const oldCentre =
    oldRadius === undefined
      ? { x: Math.floor((def.width - 1) / 2), y: Math.floor((def.height - 1) / 2) }
      : { x: oldRadius, y: oldRadius };
  const size = 2 * r + 1;
  const centre = { x: r, y: r };
  const grid = { width: size, height: size, def: { grid: 'hex' as const } };
  const [oq, or] = toCube(oldCentre.x, oldCentre.y);
  const [nq, nr] = toCube(centre.x, centre.y);
  const toNew = (x: number, y: number): Coord | null => {
    const [q, rr] = toCube(x, y);
    const c = fromCube(q - oq + nq, rr - or + nr);
    return cellDistance(grid, centre, c) <= r ? c : null;
  };
  const toOld = (x: number, y: number): Coord => {
    const [q, rr] = toCube(x, y);
    return fromCube(q - nq + oq, rr - nr + or);
  };
  const kept = (o: Coord) =>
    inside(def, o.x, o.y) &&
    (oldRadius === undefined || cellDistance(dims(def), oldCentre, o) <= oldRadius);
  const cells = Array.from({ length: size }, (_, y) =>
    Array.from({ length: size }, (_, x) => {
      if (cellDistance(grid, centre, { x, y }) > r) return '#';
      const o = toOld(x, y);
      return kept(o) ? charAt(def, o.x, o.y) : fillChar('hex', o.x, o.y);
    }).join(''),
  );
  return {
    ...def,
    width: size,
    height: size,
    cells,
    powerPoints: def.powerPoints.flatMap(([x, y]) => {
      const n = toNew(x, y);
      return n ? [[n.x, n.y] as [number, number]] : [];
    }),
    walls: (def.walls ?? []).flatMap(([[ax, ay], [bx, by]]) => {
      const a = toNew(ax, ay);
      const b = toNew(bx, by);
      return a && b
        ? [
            [
              [a.x, a.y],
              [b.x, b.y],
            ] as [[number, number], [number, number]],
          ]
        : [];
    }),
    placements: def.placements.flatMap((p) => {
      const n = toNew(p.x, p.y);
      return n ? [{ ...p, x: n.x, y: n.y }] : [];
    }),
  };
}

/** Switches between squares and hexes; walls that no longer join neighbours are dropped. */
export function setGrid(def: BoardDef, grid: GridType): BoardDef {
  const next: BoardDef = { ...def };
  if (grid === 'hex') next.grid = 'hex';
  else delete next.grid;
  const dims = { width: next.width, height: next.height, def: next };
  next.walls = (next.walls ?? []).filter(([[ax, ay], [bx, by]]) =>
    areNeighbours(dims, { x: ax, y: ay }, { x: bx, y: by }),
  );
  return next;
}

/**
 * Everything that keeps the board from being played, friendliest first: the editor's own checks
 * on sides and pieces, then whatever else the core validator objects to.
 */
export function boardProblems(def: BoardDef): string[] {
  const problems: string[] = [];
  if (def.name.trim().length === 0) problems.push('The board needs a name.');
  if (def.factions.length < 2) problems.push('A board needs at least two sides.');
  const grid = gridOf({ width: def.width, height: def.height, def });
  if (def.factions.length > maxFactions(grid))
    problems.push(`A ${grid} board seats at most ${maxFactions(grid)} sides.`);
  for (const f of def.factions) {
    if (f.name.trim().length === 0) problems.push('Every side needs a name.');
    else if (!def.placements.some((p) => p.faction === f.id))
      problems.push(`${f.name} needs at least one piece.`);
  }
  if (problems.length > 0) return problems;
  try {
    validateBoardDef(def);
  } catch (err) {
    problems.push(err instanceof BoardDefError ? err.message : String(err));
  }
  return problems;
}

/**
 * Advice that does not keep the board from being played: the table frames a hex board as a
 * hexagon (and hexagons are what New board makes), so a hex board that is not one is told so.
 */
export function boardHints(def: BoardDef): string[] {
  const hints: string[] = [];
  if (gridOf(dims(def)) === 'hex' && hexagonRadius(def) === undefined)
    hints.push(
      def.width === def.height && def.width % 2 === 1
        ? `Hex boards are framed as hexagons on the table; this one has hexes beyond ${(def.width - 1) / 2} rings from the centre. Paint them void or set a radius.`
        : `Hex boards are framed as hexagons on the table; this one is a ${def.width}×${def.height} rectangle of hexes. Set a radius to make it a hexagon.`,
    );
  return hints;
}

export function initialEditorState(
  def = newBoardDef({ name: 'New board', grid: 'square', width: 9, height: 9, takenIds: [] }),
): EditorState {
  return {
    def,
    tool: 'paint',
    brush: 'L',
    faction: 0,
    character: '',
    wallFrom: null,
    dirty: false,
  };
}

export function editorReducer(state: EditorState, action: EditorAction): EditorState {
  const edit = (def: BoardDef): EditorState =>
    def === state.def ? state : { ...state, def, dirty: true };
  switch (action.type) {
    case 'undo':
    case 'redo':
      return state; // History is owned by EditorStore, not the pure editing reducer.
    case 'new':
      return { ...initialEditorState(newBoardDef(action)), character: state.character };
    case 'load':
      return { ...state, def: action.def, wallFrom: null, faction: 0, dirty: false };
    case 'saved':
      return { ...state, dirty: false };
    case 'tool':
      return { ...state, tool: action.tool, wallFrom: null };
    case 'brush':
      return { ...state, brush: action.brush, tool: 'paint', wallFrom: null };
    case 'piece':
      return {
        ...state,
        tool: 'place',
        wallFrom: null,
        faction: action.faction ?? state.faction,
        character: action.character ?? state.character,
      };
    case 'character':
      return state.character === action.character
        ? state
        : { ...state, character: action.character };
    case 'cell':
      return cellPicked(state, { x: action.x, y: action.y });
    case 'rename':
      return edit({ ...state.def, name: action.name });
    case 'luminosity': {
      const next = { ...state.def };
      if (action.value) next.luminosity = action.value;
      else delete next.luminosity;
      return edit(next);
    }
    case 'describe':
      return edit(
        action.description.trim()
          ? { ...state.def, description: action.description }
          : withoutDescription(state.def),
      );
    case 'resize':
      return { ...edit(resizeBoard(state.def, action.width, action.height)), wallFrom: null };
    case 'radius':
      return { ...edit(setHexRadius(state.def, action.radius)), wallFrom: null };
    case 'grid':
      return { ...edit(setGrid(state.def, action.grid)), wallFrom: null };
    case 'add-faction':
      return edit(addFaction(state.def));
    case 'remove-faction': {
      const next = edit(removeFaction(state.def));
      return { ...next, faction: Math.min(next.faction, next.def.factions.length - 1) };
    }
    case 'faction':
      return edit(updateFaction(state.def, action.index, action.patch));
  }
}

function withoutDescription(def: BoardDef): BoardDef {
  const { description: _dropped, ...rest } = def;
  return rest;
}

function cellPicked(state: EditorState, at: Coord): EditorState {
  const { def } = state;
  switch (state.tool) {
    case 'paint':
      return touched(state, paintCell(def, at.x, at.y, state.brush));
    case 'power':
      return touched(state, togglePowerPoint(def, at.x, at.y));
    case 'place': {
      const faction = def.factions[state.faction];
      if (!faction || !state.character) return state;
      return touched(state, placePiece(def, at.x, at.y, faction.id, state.character));
    }
    case 'wall': {
      if (!state.wallFrom) return { ...state, wallFrom: at };
      if (same(state.wallFrom, at)) return state;
      const next = toggleWall(def, state.wallFrom, at);
      // A cell that is not a neighbour becomes the new first pick.
      return next === def
        ? { ...state, wallFrom: at }
        : { ...state, def: next, dirty: true, wallFrom: null };
    }
  }
}

function touched(state: EditorState, def: BoardDef): EditorState {
  return def === state.def ? state : { ...state, def, dirty: true };
}

/**
 * The run an edit belongs to, for the edits made a little at a time: a name, a description, a
 * side's name or colour typed a letter at a time, and the cells painted in one drag. `null` for
 * every other edit.
 */
function runKey(action: EditorAction): string | null {
  if (action.type === 'rename' || action.type === 'describe') return action.type;
  if (action.type === 'faction')
    return `faction:${action.index}:${Object.keys(action.patch).sort().join(',')}`;
  if (action.type === 'cell') return 'stroke';
  return null;
}

/** The editor's state outlives its panel, so a half-drawn board survives a trip to the game. */
export class EditorStore {
  private state: EditorState;
  private past: BoardDef[] = [];
  private future: BoardDef[] = [];
  /**
   * The run of edits under way, and whether its undo step is recorded yet: each keystroke of a
   * name and each cell of a drag joins the step its first change made.
   */
  private run: { key: string; recorded: boolean } | null = null;
  private readonly listeners = new Set<() => void>();

  constructor(initial: EditorState = initialEditorState()) {
    this.state = initial;
  }

  getSnapshot(): EditorState {
    return this.state;
  }

  dispatch(action: EditorAction): void {
    let next: EditorState;
    if (action.type === 'undo' || action.type === 'redo') {
      const source = action.type === 'undo' ? this.past : this.future;
      const target = action.type === 'undo' ? this.future : this.past;
      const def = source.pop();
      if (!def) return;
      target.push(this.state.def);
      this.run = null;
      next = { ...this.state, def, dirty: true, wallFrom: null };
    } else {
      next = editorReducer(this.state, action);
      if (action.type === 'load' || action.type === 'new') {
        this.past = [];
        this.future = [];
        this.run = null;
      } else {
        // A name typed a letter at a time, or a stroke of paint, is one step to undo, not one per
        // letter or cell crowding the board's other edits out of the fifty kept (editor:bugs#8).
        const key = runKey(action);
        const joins =
          key !== null &&
          key === this.run?.key &&
          (action.type !== 'cell' || action.stroke === true);
        if (!joins) this.run = key === null ? null : { key, recorded: false };
        if (next.def !== this.state.def) {
          if (!this.run?.recorded) this.past = [...this.past.slice(-49), this.state.def];
          if (this.run) this.run.recorded = true;
          this.future = [];
        }
      }
    }
    if (next === this.state) return;
    this.state = next;
    for (const listener of this.listeners) listener();
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
}

export function useEditor(store: EditorStore): EditorState {
  return useSyncExternalStore(
    (listener) => store.subscribe(listener),
    () => store.getSnapshot(),
    () => store.getSnapshot(),
  );
}
